/* eslint-disable no-underscore-dangle */
import {useQuery} from '@apollo/client';
import {Box, FormControl, FormControlLabel, Checkbox, TextField} from '@mui/material';
import {Email} from '@mui/icons-material';
import React, {useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {
  GET_ACCOUNTS,
  GET_LEADS_AND_ACCOUNTS,
} from '../../components/AccountOverview/accountOverviewQueries';
import {SearchResults} from '../../components/Common/SearchResults';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {useDebounce} from '../../hooks/useDebounce';
import {useRecentClients} from '../../hooks/useRecentClients';
import useTitle from '../../hooks/useTitle';
import {accounts, accountsVariables} from '../../schema-types/accounts';
import {darkBlue, themeColors} from '../../theme/Theme';
import {
  LeadsAndAccounts,
  LeadsAndAccountsVariables,
  LeadsAndAccounts_leadsAndAccounts,
} from '../../schema-types/LeadsAndAccounts';
import {gridCellDef} from '../../utils/GridCellDef';

export const RenderAccountNameAndEmail: gridCellDef<LeadsAndAccounts_leadsAndAccounts> = (
  params
) => {
  const account = params.row;
  const email =
    account.__typename === 'AccountType' ? account.primaryContact?.email : null;
  return (
    <div
      style={{
        flex: 1,
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
        color: darkBlue,
        textDecoration: 'none',
        paddingLeft: 20,
        borderLeft: `3px solid ${themeColors.blue}`,
        lineHeight: 1,
        height: '100%',
      }}
    >
      <Link
        role="a"
        className="sentry-mask"
        to={{
          pathname:
            account.__typename === 'LeadType'
              ? `/leads/${params.id}/basic`
              : `/account/${params.id}`,
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={{fontWeight: 600, color: darkBlue, textDecoration: 'none'}}
      >
        <>{account.name || ''} </>
      </Link>
      {params.row.isDeleted ? '(inactive)' : ''}
      {email ? (
        <a
          className="sentry-mask"
          onClick={(e) => e.stopPropagation()} // disable row navigation event
          style={{
            color: darkBlue,
            textAlign: 'center',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
          href={`mailto:${email}`}
        >
          <Email style={{marginRight: 4}} fontSize="small" />
          {email}
        </a>
      ) : null}
    </div>
  );
};

const devPreferredStartEmails: Record<string, string> = {
  // FEEL FREE TO ADD YOUR EMAIL IF YOU PREFER TO HAVE ACCOUNTS LOAD WITH PARTICULAR ACCOUNTS
  // 'saturnino.harris@saltus.co.uk': 'test account 7',
};

export const AccountSearchPage: React.FC = () => {
  useTitle('Client search');
  const navigate = useNavigate();
  const {djangoUser} = useDjangoContext();
  const [search, setSearch] = useState<string>(
    djangoUser.email
      ? devPreferredStartEmails[(djangoUser.email as string).toLowerCase()] || ''
      : ''
  );
  const [includeLeads, setIncludeLeads] = useState(false);
  const {previousSearches} = useRecentClients();
  const delayedSearch = useDebounce(search, 1000);

  const {
    loading: accountsLoading,
    error: accountsError,
    data: accountsData,
    refetch: refetchAccountsOnly,
  } = useQuery<accounts, accountsVariables>(GET_ACCOUNTS, {
    variables: {
      search: delayedSearch,
      curoIds: delayedSearch.length
        ? null
        : (previousSearches || []).filter((x) => x.curoId).map((x) => x.curoId),
    },
    skip: includeLeads && delayedSearch.length < 3 && !previousSearches.length,
  });

  const {
    loading: accountsAndLeadsLoading,
    error: accountsAndLeadsError,
    data: accountsAndLeadsData,
    refetch: refetchAccountsAndLeads,
  } = useQuery<LeadsAndAccounts, LeadsAndAccountsVariables>(GET_LEADS_AND_ACCOUNTS, {
    variables: {
      search: delayedSearch,
    },
    skip: !includeLeads || (delayedSearch.length < 3 && !previousSearches.length),
  });

  useEffect(() => {
    if (includeLeads) {
      refetchAccountsAndLeads({
        search: delayedSearch,
      });
    } else {
      refetchAccountsOnly({
        search: delayedSearch,
      });
    }
  }, [includeLeads]);

  const searchResults = includeLeads
    ? accountsAndLeadsData?.leadsAndAccounts
    : accountsData?.accounts;

  return (
    <>
      <Box mb={4} display="flex" flexDirection="row" alignContent="flex-end">
        <TextField
          value={search}
          autoFocus
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search accounts"
          style={{minWidth: 400, flex: 1}}
          margin="normal"
          inputProps={{
            type: 'search',
          }}
        />
        <Box ml={4} />
        <FormControl onClick={() => setIncludeLeads((x) => !x)}>
          <FormControlLabel
            label="Include leads?"
            control={<Checkbox checked={includeLeads || false} />}
          />
        </FormControl>
      </Box>

      <SearchResults
        dataGridId="OldAccountSearch"
        title="Accounts"
        loading={includeLeads ? accountsAndLeadsLoading : accountsLoading}
        search={search}
        columns={[]}
        data={searchResults || []}
        onRowDoubleClick={(params) => {
          const type = params.row.__typename;
          if (type === 'LeadType') {
            navigate({pathname: `/leads/${params.row.id}/basic`});
          } else {
            navigate({pathname: `/account/${params.row.id}`});
          }
        }}
      />
    </>
  );
};
